import { BaseTextFieldProps, InputBaseProps, TextField, useForkRef } from '@mui/material';
import { Control, RegisterOptions, useController } from 'react-hook-form';
import { useFieldStateError } from './useFieldStateError';
import { ChangeEvent, FocusEvent, useCallback } from 'react';
import { sanitizeSpaces } from '@razz-team/utils';

export type FormTextFieldProps = Omit<
  BaseTextFieldProps,
  'name' | 'value' | 'required' | 'disabled' | 'onChange'
> & {
  name: string;
  filter?: (value: string) => boolean;
  control?: Control;
  InputProps?: InputBaseProps;
} & Pick<
    RegisterOptions,
    'min' | 'max' | 'pattern' | 'required' | 'disabled' | 'validate' | 'minLength' | 'maxLength'
  >;

export function FormTextField({
  name,
  control,
  min,
  max,
  pattern,
  filter,
  validate,
  required,
  minLength,
  maxLength,
  inputRef,
  error,
  onBlur,
  helperText,
  ...props
}: FormTextFieldProps) {
  const { field, fieldState } = useController({
    name,
    control,
    rules: {
      min,
      max,
      pattern,
      validate,
      required,
      minLength,
      maxLength,
    },
  });

  const fieldError = useFieldStateError(props.label, fieldState.error);
  const forkedInputRef = useForkRef(field.ref, inputRef);

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      if (!filter || filter(event.target.value)) {
        field.onChange(event);
      }
    },
    [field, filter],
  );

  const handleBlur = useCallback(
    (e: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      if (field.value) {
        field.onChange(sanitizeSpaces(field.value));
      }

      field.onBlur();
      onBlur?.(e);
    },
    [field, onBlur],
  );

  return (
    <TextField
      {...props}
      inputRef={forkedInputRef}
      name={field.name}
      value={field.value ?? ''}
      onBlur={handleBlur}
      onChange={handleChange}
      required={Boolean(required)}
      error={error || fieldState.invalid}
      helperText={error ? helperText : (fieldError ?? helperText)}
      slotProps={{
        htmlInput: { 'data-1p-ignore': props.autoComplete === 'off', ...props.inputProps },
      }}
    />
  );
}
